import { Button } from 'antd'
import Head from 'next/head'
import { FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import Layout from '@/components/billboard/layout'
import BannerSection, {
  InfoWrapper,
} from '@/components/public-page/banner-section'
import useUrlControl from '@/components/public-page/hook/use-url-control'
import RecommendationSection from '@/components/public-page/recommendation-section'
import { useAuth } from '@/hooks/use-auth'
import { Page } from '@/hooks/use-authorization/constants'
import { useIntl } from '@/i18n/hooks/use-intl'
import { ampli, ClicksRegisterProperties } from '@/utils/ampli'

interface LinkListLayoutProps {
  children: ReactNode
  from?: ClicksRegisterProperties['from']
}

export const billboardMultiDimensionUtmInfo = {
  utm_source: 'billboard',
  utm_medium: 'content',
  utm_campaign: 'billboard_content_register',
} as const

const LinkListLayout: FunctionComponent<LinkListLayoutProps> = ({
  children,
  from = 'home',
}) => {
  const { formatMessage } = useIntl()
  const { getAppUrl } = useUrlControl()
  const { isAd } = useAuth()

  return (
    <Layout>
      <Wrapper>
        <Head>
          <title>
            {formatMessage({ id: 'landing:billboard_link_list_seo_title' })}
          </title>
          <meta
            content={formatMessage({
              id: 'landing:billboard_link_list_seo_description',
            })}
            name='description'
          />
        </Head>
        <BannerSection>
          <Description>
            {formatMessage({
              id: 'landing:team_intro_category_ai_description',
            })}
          </Description>
          <Button
            block={false}
            href={
              isAd
                ? getAppUrl(Page.WorkspacePlan, {})
                : getAppUrl(Page.Register, {
                    from,
                    ...billboardMultiDimensionUtmInfo,
                  })
            }
            size='large'
            target='_blank'
            type='primary'
            onClick={(): void => {
              ampli.clicksRegister({
                from,
              })
            }}
          >
            {formatMessage({
              id: 'landing:general_btn_text_trail_experience_now',
            })}
          </Button>
        </BannerSection>
        {children}
        <RecommendationSection
          from={from}
          registerSearchParams={{
            ...billboardMultiDimensionUtmInfo,
            from,
          }}
        />
      </Wrapper>
    </Layout>
  )
}

export default LinkListLayout

const Description = styled.div`
  font-size: 14px;
  line-height: 140%;
  font-weight: 400;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const Wrapper = styled.div`
  ${InfoWrapper} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }

  @media (max-width: 768px) {
    ${InfoWrapper} {
      align-items: center;
    }

    ${Description} {
      text-align: center;
    }
  }
`
